import { useCallback } from 'react'
import { useHistory } from 'react-router'
import { OpportunityResponse, OpportunityResponseWithRecurrenceInfo } from 'Event/interfaces/interfaceCreateEditEvent'
import { useFeatureEnabled } from 'core/feature/hooks'
import { useShowEventAddEditPrompt } from 'Event/hooks/useShowEventAddEditPrompt'
import { useShowAddEditOfferPrompt } from 'Event/hooks/useShowAddEditOfferPrompt'
import useAddEditLocationPrompt from 'locations/hooks/useAddEditLocationPrompt'
import { useFetchOpportunityLocation } from 'locations/hooks/useFetchOpportunityLocation'
import { OpportunityOccurrencePersonGroup } from 'Event/interfaces/interfaceCreateEditEvent'

export type EventDrilldownEditScreen = 1 | 2

export const useEventDrilldownEdit = ({
  event,
  encodedOccurrence,
  personGroups,
  onSuccess,
}: {
  event: OpportunityResponseWithRecurrenceInfo
  encodedOccurrence: string
  personGroups: OpportunityOccurrencePersonGroup[]
  onSuccess: (event: OpportunityResponse) => void
}) => {
  const history = useHistory()
  const isNewEditEnabled = useFeatureEnabled('NewEditEventUICalendarEvent')
  const isEditMultishiftFallbackEnabled = useFeatureEnabled('NewAddEventUILegacyFallback')
  const offersEnabled = useFeatureEnabled('StandaloneOffers')
  const showOpportunityPrompt = useShowEventAddEditPrompt(onSuccess)
  const showOfferPrompt = useShowAddEditOfferPrompt(onSuccess)
  const showLocationEditPrompt = useAddEditLocationPrompt()
  const { fetchLocation } = useFetchOpportunityLocation()

  const usesModalEdit = !(
    event.isOffer ||
    (event.schedulable &&
      (!isNewEditEnabled ||
        (isEditMultishiftFallbackEnabled && event.timeshifts?.length && event.timeshifts.length > 1)))
  )

  const openLegacyEdit = useCallback(() => {
    if (event.isOffer) {
      showOfferPrompt({ offer: event, visibilityGroups: personGroups, encodedOccurrence })
    } else if (
      event.schedulable &&
      (!isNewEditEnabled ||
        (isEditMultishiftFallbackEnabled && event.timeshifts?.length && event.timeshifts.length > 1))
    ) {
      history.push(`/events/${encodedOccurrence}/edit-event/basic`)
    } else if (!event.schedulable && offersEnabled) {
      fetchLocation(event.locationId).then(location =>
        showLocationEditPrompt(location, true).then(result => onSuccess(result as OpportunityResponse)),
      )
    } else {
      showOpportunityPrompt(event)
    }
  }, [
    encodedOccurrence,
    event,
    fetchLocation,
    personGroups,
    history,
    isEditMultishiftFallbackEnabled,
    isNewEditEnabled,
    offersEnabled,
    onSuccess,
    showLocationEditPrompt,
    showOfferPrompt,
    showOpportunityPrompt,
  ])

  const openModalAtScreen = useCallback(
    (initialScreen: EventDrilldownEditScreen) => {
      if (usesModalEdit) {
        showOpportunityPrompt(event, { initialScreen })
      } else {
        openLegacyEdit()
      }
    },
    [event, openLegacyEdit, showOpportunityPrompt, usesModalEdit],
  )

  const onEditEventInformation = useCallback(() => openModalAtScreen(1), [openModalAtScreen])

  const onEditRegistrationDetails = useCallback(() => openModalAtScreen(2), [openModalAtScreen])

  return {
    onEditEventInformation,
    onEditRegistrationDetails,
    openLegacyEdit,
    usesModalEdit,
  }
}
